/**
 * Constants for the Insights Bot knowledge-base console (/insightsbot/knowledgebase).
 *
 * The console is an internal engineering tool: it reads the Reports KPI → API
 * mapping this repo already maintains (the /KPI/API/mappings page), turns each
 * KPI into the `knowledge` payload the Reports BE expects, diffs that against
 * the live knowledge base and lets an engineer review and fire the cURLs.
 *
 * NOT merchant-facing — everything about whether it is reachable, and which
 * host it talks to, lives here.
 */

/* ═══════════════════════════════════════════════════════════════════════════
   ROUTE FLAG
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * KILL SWITCH for /insightsbot/knowledgebase. Nothing else gates this route.
 *
 * Flip it to `false` on any build that must not be able to write to a knowledge
 * base — the console can POST / PUT / DELETE against whatever host the reader
 * types into the base-URL box, so on a production bundle it is a write endpoint
 * pointed at an arbitrary target.
 *
 * When false, TWO things happen and both matter:
 *
 *   1. `App.tsx` does not REGISTER the route at all. `/insightsbot/knowledgebase`
 *      then falls through to the catch-all and redirects home, and the screen's
 *      lazy chunk is never fetched — so the console's code is not sitting in the
 *      browser waiting to be found.
 *   2. The screen itself still redirects to /reports if it is somehow rendered —
 *      a direct import, or someone re-adding a route without reading this.
 *
 * Belt and braces on purpose: (1) is the switch, (2) is the guard that survives
 * someone wiring the component up a second way.
 */
export const enableInsightsBotKnowledgeBase = true;

/* ═══════════════════════════════════════════════════════════════════════════
   HOST
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * The knowledge base lives on the NestJS Reports BE (maghil-reports-be), which
 * is a DIFFERENT service from `REACT_APP_API_ENDPOINT_REPORTS` (the Java
 * magilhub-data-services-reports host every report screen calls). There is no
 * shared env var for it, so the default is the local dev port and the reader
 * overrides it in the page (persisted per browser).
 */
export const INSIGHTS_BOT_KB_DEFAULT_BASE_URL = (
  process.env.REACT_APP_INSIGHTS_BOT_KB_API || 'http://localhost:4000'
)
  .replace(/"/g, '')
  .trim()
  .replace(/\/$/, '');

/** localStorage key holding the reader's base-URL override. */
export const INSIGHTS_BOT_KB_BASE_URL_STORAGE_KEY = 'insightsBotKbBaseUrl';

/** Controller path — `@Controller('api/insights-bot/knowledge-base')` on the BE. */
export const INSIGHTS_BOT_KB_PATH = '/api/insights-bot/knowledge-base';

/** `POST /api/insights-bot/knowledge-base/reload-cache` — refreshes the Redis blob. */
export const INSIGHTS_BOT_KB_RELOAD_CACHE_PATH = `${INSIGHTS_BOT_KB_PATH}/reload-cache`;

/* ═══════════════════════════════════════════════════════════════════════════
   SCOPE
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Report pages the console selects on first load — the two screens whose KPIs
 * are being re-pointed at the v2 / ClickHouse endpoints. Every other page in
 * the mapping can still be ticked by hand; these are only the default.
 *
 * Keys must match `reportsKPIAPIMappings` exactly.
 */
export const INSIGHTS_BOT_KB_DEFAULT_SCOPE = ['Product & Menu Performance', 'EOD Summary'];

/** Page heading / mobile TopBar title. */
export const INSIGHTS_BOT_KB_TITLE = 'Insights Bot Knowledge Base';
